import { Injectable } from "@angular/core";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { of } from "rxjs";
import { switchMap, catchError, exhaustMap, map, tap } from "rxjs/operators";
import {
  organizationsListLoad,
  organizationsListLoadSuccess,
  organizationsListLoadFail,
  organizationChatSettingsLoad,
  organizationChatSettingsLoadSuccess,
  organizationChatSettingsLoadFail,
  organizationChatSettingsUpdate,
  organizationChatSettingsUpdateSuccess,
  organizationChatSettingsUpdateFail,
} from "../actions/organizations.actions";
import { OrganizationsService } from "src/app/shared/services/organizations/organizations.service";
import { Organization } from "src/app/shared/interfaces/organization";
import { PdfReportService } from "src/app/shared/services/pdfreport/pdfreport.service";
import { PdfreportLoaderService } from "src/app/modules/orgadmin/services/pdfreport-loader/pdfreport-loader.service";
import {
  organizationRaportLoad,
  organizationRaportLoadFail,
  organizationRaportLoadSuccess,
} from "../actions/admin/organization-raport.actions";
import {
  rankingOrganizationLoad,
  rankingOrganizationLoadSuccess,
  rankingOrganizationFailure,
} from "../actions/ranking-organization.actions";
import { NotificationsService } from "src/app/shared/services/notifications/notifications.service";
import { createNotification } from "src/app/shared/utils/create-notification";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class OrganizationsEffect {
  loadOrganizationChatSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationChatSettingsLoad),
      switchMap(({ organizationId }) =>
        this.organizationsService.getChatSettings(organizationId).pipe(
          map(({ chat_scope, chat_enabled }) =>
            organizationChatSettingsLoadSuccess({
              organizationId,
              chatScope: chat_scope,
              chatEnabled: chat_enabled,
            }),
          ),
          catchError((error) =>
            of(organizationChatSettingsLoadFail({ organizationId, error })),
          ),
        ),
      ),
    ),
  );

  updateOrganizationChatSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationChatSettingsUpdate),
      exhaustMap(
        ({
          organizationId,
          chatScope,
          chatEnabled,
          previousChatScope,
          previousChatEnabled,
        }) =>
          this.organizationsService
            .updateChatSettings(organizationId, chatScope, chatEnabled)
            .pipe(
              map(({ chat_scope, chat_enabled }) =>
                organizationChatSettingsUpdateSuccess({
                  organizationId,
                  chatScope: chat_scope,
                  chatEnabled: chat_enabled,
                }),
              ),
              catchError((error) =>
                of(
                  organizationChatSettingsUpdateFail({
                    organizationId,
                    previousChatScope,
                    previousChatEnabled,
                    error,
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  getCoursesAdminList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationsListLoad),
      exhaustMap(() => {
        return this.organizationsService.getOrganizations().pipe(
          map((res: Organization[]) =>
            organizationsListLoadSuccess({ organizations: res }),
          ),
          catchError(({ error }) => of(organizationsListLoadFail(error))),
        );
      }),
    ),
  );

  downloadOrganizationReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationRaportLoad),
      switchMap((param) => {
        return this.pdfreportService
          .downloadOrganizationReport(param.OrganizationId)
          .pipe(
            map((pdf) => organizationRaportLoadSuccess({ pdf })),
            catchError(({ error }) => {
              this.pdfreportLoaderService.setPdfReportLoader(false);
              return of(organizationRaportLoadFail(error));
            }),
          );
      }),
    ),
  );

  setRankingInOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rankingOrganizationLoad),
      switchMap((param) =>
        this.organizationsService
          .setRankingInOrganization(param.OrganizationId, param.in_ranking)
          .pipe(
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(param.notificationMessage),
                  },
                }),
              );
            }),
            map((in_ranking: boolean) =>
              rankingOrganizationLoadSuccess({ in_ranking }),
            ),
            catchError(({ error }) => of(rankingOrganizationFailure(error))),
          ),
      ),
    ),
  );

  constructor(
    private organizationsService: OrganizationsService,
    private actions$: Actions,
    private pdfreportService: PdfReportService,
    private pdfreportLoaderService: PdfreportLoaderService,
    private notificationsService: NotificationsService,
    private translate: TranslateService,
  ) {}
}
